import { Search, Wrench, ShieldCheck } from "lucide-react";

const phases = [
  {
    icon: Search,
    tag: "Phase 1",
    title: "Assess",
    body:
      "We identify what is exposed, what is misconfigured, and what is actively violating your compliance posture. Every finding is risk-rated and mapped to the relevant regulatory framework — HIPAA, SOC 2, NIST AI RMF, CPRA/CCPA, GDPR, or CMMC.",
    deliverable: "Findings report with prioritized remediation roadmap.",
  },
  {
    icon: Wrench,
    tag: "Phase 2",
    title: "Remediate",
    body:
      "We fix what we find. Configuration changes, code-level fixes, platform deployments, and GTM or consent management updates are all implemented and validated before going to production. We document every change.",
    deliverable: "Remediated implementation with full change log.",
  },
  {
    icon: ShieldCheck,
    tag: "Phase 3",
    title: "Validate",
    body:
      "We prove it is working. Independent testing, evidence package assembly, and rescan coordination give you an artifact that holds up to regulators, insurance carriers, and auditors.",
    deliverable: "Validation report suitable for regulatory, insurance, or board-level review.",
  },
];

export default function ArqAbout() {
  return (
    <section id="about" className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="arq-label mb-6">Who We Are</div>
        <h2
          className="font-display font-semibold text-arq-text mb-10 max-w-[900px]"
          style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
        >
          Independent Verification for AI, Application, and Privacy Risk.
        </h2>

        <div className="grid md:grid-cols-5 gap-12 mb-20">
          <div className="md:col-span-3 space-y-5 text-arq-muted text-[17px] leading-[1.75]">
            <p>
              Most organizations deploying AI, building applications, or collecting user data have
              never had their security and privacy posture independently verified. Not by a
              practitioner. Not against the actual threat model. Not with evidence that holds up to
              regulatory or insurance scrutiny.
            </p>
            <p className="text-arq-text">ArqeraAI was built to close that gap.</p>
            <p>
              Founded by practitioners with experience securing enterprise applications, AI
              systems, and regulated data environments across logistics, healthcare, and financial
              services, ArqeraAI brings hands-on engineering depth to every engagement. Our
              practitioners hold CISSP, AWS, and Azure certifications and work across cloud-native, containerized, and on-premise environments, carrying 10+ years of application
              security experience across organizations where security failures carry real legal,
              regulatory, and financial liability — from regulatory enforcement actions to private
              litigation, contractual breach, and uninsurable loss.
            </p>
            <p>
              We are not a platform. We are not a Big 4 consultancy. We are engineers who have
              shipped production security programs, responded to real incidents, and built the
              tooling that sits between a finding and a fix. Every engagement produces
              documentation that holds up to regulatory scrutiny, insurance review, and board-level
              reporting — not just a PDF that gets filed and forgotten.
            </p>
            <p className="text-[14px] text-arq-tertiary mt-8 pt-6 border-t border-arq-border">
              ArqeraAI is an operating subsidiary of Batach Global, an operationally focused
              holding company that builds and scales businesses through disciplined execution and
              intelligent systems.
            </p>
          </div>

          <div className="md:col-span-2 space-y-5">
            <div className="arq-card p-8">
              <div className="font-display text-[28px] font-bold text-arq-text mb-2">10+ Years</div>
              <div className="text-arq-muted text-[14px] leading-[1.5]">
                Application security experience across regulated industries
              </div>
            </div>
            <div className="arq-card p-8">
              <div className="font-display text-[28px] font-bold text-arq-text mb-2">Full Stack</div>
              <div className="text-arq-muted text-[14px] leading-[1.5]">
                Application security · AI red teaming · Privacy enforcement · Compliance documentation
              </div>
            </div>
            <div className="arq-card p-8">
              <div className="font-display text-[28px] font-bold text-arq-text mb-2">3 Phases</div>
              <div className="text-arq-muted text-[14px] leading-[1.5]">
                Assess · Remediate · Validate — a repeatable methodology, not a one-off report
              </div>
            </div>
          </div>
        </div>

        {/* How We Work — 3 Phases */}
        <div className="mt-4">
          <div className="arq-label mb-4">How We Work</div>
          <h3
            className="font-display font-semibold text-arq-text mb-10 max-w-[820px]"
            style={{ fontSize: "clamp(24px, 3vw, 32px)", lineHeight: 1.25 }}
          >
            A repeatable, three-phase engagement model.
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {phases.map((p) => (
              <div key={p.title} className="arq-card p-8 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md bg-arq-blue/10 border border-arq-blue/30 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-arq-blue" />
                  </div>
                  <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary">
                    {p.tag}
                  </div>
                </div>
                <h4 className="font-display text-[22px] font-semibold text-arq-text mb-3">
                  {p.title}
                </h4>
                <p className="text-arq-muted text-[15px] leading-[1.7] mb-5 flex-1">{p.body}</p>
                <div className="pt-4 border-t border-arq-border">
                  <div className="font-mono-tech text-[11px] tracking-[0.08em] uppercase text-arq-tertiary mb-1.5">
                    Deliverable
                  </div>
                  <div className="text-arq-text text-[14px] leading-[1.55]">{p.deliverable}</div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center font-display text-arq-text text-[20px] md:text-[22px] mt-12 max-w-[720px] mx-auto">
            We do not write reports and walk away.{" "}
            <span className="text-arq-blue">We fix problems and prove they are fixed.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
